import type { ArtifactRef } from '../../state/chatTypes';

/** `shareType` values the share endpoint accepts for a single answer. */
export const SHARE_TYPE = {
  RESPONSE: 'response',
  TABLE: 'table',
  VISUALIZATION: 'visualization',
} as const;

export type ShareType = (typeof SHARE_TYPE)[keyof typeof SHARE_TYPE];

/** The artifact kind each table or chart share carries. */
const ARTIFACT_KIND_BY_SHARE_TYPE: Partial<
  Record<ShareType, ArtifactRef['kind']>
> = {
  [SHARE_TYPE.TABLE]: 'table',
  [SHARE_TYPE.VISUALIZATION]: 'chart',
};

/** What an answer shows besides its text. */
export type ShareableContent = {
  hasTable: boolean;
  hasChart: boolean;
};

/**
 * Picks what sharing an answer sends.
 *
 * A share carries at most one table or one chart, so an answer showing
 * both is shared as its chart, whose email also carries the answer text.
 *
 * @param content - Whether the answer shows a table and whether it shows a chart.
 * @returns The `shareType` to send.
 */
export const getShareType = ({
  hasTable,
  hasChart,
}: ShareableContent): ShareType => {
  if (hasChart) {
    return SHARE_TYPE.VISUALIZATION;
  }
  if (hasTable) {
    return SHARE_TYPE.TABLE;
  }
  return SHARE_TYPE.RESPONSE;
};

/**
 * Finds the table or chart a share carries among an answer's artifacts.
 * A chart share carries the answer's first chart, the one the API treats
 * as primary.
 *
 * @param artifacts - The answer's tables and charts, in the order it showed them.
 * @param shareType - What the share sends.
 * @returns The artifact id, or `undefined` for a response share or when
 * the answer has no artifact of that kind.
 */
export const findShareArtifactId = (
  artifacts: ReadonlyArray<ArtifactRef> | undefined,
  shareType: ShareType
): string | undefined => {
  const kind = ARTIFACT_KIND_BY_SHARE_TYPE[shareType];
  if (kind === undefined) {
    return undefined;
  }
  return artifacts?.find((artifact) => artifact.kind === kind)?.id;
};
